import {
  FluidButtonConfig,
  FluidTabChange,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Listen,
  Method,
  Prop,
  readTask,
  State,
  Watch,
} from '@stencil/core';
import { Observable, timer } from 'rxjs';

import {
  ACTIVE_HIDDEN_TAB,
  HIDDEN_ACTIVE_TAB,
  NO_TAB_IDS,
  NO_TAB_WITH_ID,
} from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import {
  ARROW_KEYS,
  ARROW_LEFT,
  ARROW_UP,
  EventUtils,
  TAB,
} from '../../utils/events/event-utils';
import { Utils } from '../../utils/utils';
import {
  AddTabButton,
  FluidTabsTranslationOptions,
  TabButtonsContainer,
} from './components/fluid-tabs-components';

/**
 * @displayName Tabs
 */
@Component({
  tag: 'fluid-tabs',
  styleUrl: 'fluid-tabs.less',
  shadow: true,
})
export class FluidTabs implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Reference to the "Tabs list" container element.
   */
  _tabsListContainer: HTMLElement;

  /**
   * Reference to the Tab Buttons container element.
   */
  _tabsList: HTMLElement;

  /**
   * Default px value that Tabs are scrolled when left/right arrow buttons
   * are clicked.
   */
  _defaultScrollLeft = 250;

  /**
   * Reference to currently selected Tab button (NOT currently selected
   * FLUID Tab child).
   */
  private _selectedBtn: HTMLElement;

  /**
   * Reference to Tab button that currently has :focus.
   */
  private _activeBtn: HTMLElement;

  /**
   * True if ResizeObserver functionality is supported, false if not.
   */
  private _resizeObserverSupported: boolean =
    typeof ResizeObserver !== 'undefined';

  /**
   * Reference to ResizeObserver instance that monitors changes to the
   * component's size & displays navigation arrows when needed. If
   * ResizeObserver isn't supported, falls back on window resize listener.
   */
  private _resizeObserver: any;

  /**
   * Reference to the MutationObserver that monitors Tabs parent for
   * changes in slotted Tab children.
   */
  private _slotObserver: MutationObserver;

  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLFluidTabsElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidTabsTranslationOptions;
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- States

  /**
   * Array of FLUID Tab children used to populate Tab buttons.
   */
  @State() tabs: HTMLFluidTabElement[];

  /**
   * Currently selected FLUID Tab child (NOT currently selected Tab button).
   */
  @State() selectedTab: HTMLFluidTabElement;

  /**
   * If true, left/right navigation "arrow buttons" are shown.
   */
  @State() showArrows = false;

  /**
   * The current x-axis scroll position of Tabs.
   */
  @State() scrollPosition = 0;

  /**
   * The maximum x-axis scroll position of Tabs.
   */
  @State() maxScrollPosition = 0;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * If false, selected Tab cannot be changed & Tabs will appear disabled.
   */
  @Prop({ mutable: true }) tabCanChange = true;

  /**
   * When provided, tabs will wait until the observable is true before allowing the
   * tab to change.
   */
  @Prop({ mutable: true }) debounceChange: Observable<boolean>;

  /**
   * If true, tab navigation arrows will be disabled (not recommended unless you are making your
   * users aware that keyboard will be their only option)
   */
  @Prop({ mutable: true }) disableNavigationArrows = false;

  /**
   * If true, Tabs will render vertically rather than horizontally.
   */
  @Prop({ mutable: true }) verticalLayout = false;

  /**
   * If true, "+" button will appear to allow creation of default new tabs.
   *
   */
  @Prop({ mutable: true }) addTabBtnConfig: FluidButtonConfig;

  @Watch('addTabBtnConfig')
  addTabBtnConfigChange() {
    readTask(() => {
      Utils.nextTick().then(() => {
        this.shouldShowTabNavigation();
      });
    });
  }

  /**
   * Maximum number of tabs that can be added using add tab button
   *
   */
  @Prop({ mutable: true }) maximumNumberOfTabs: number;

  // ================================================================ //
  // -- Events

  /**
   * Event emitted when a Tab is clicked, regardless of whether the selected
   * Tab changes.
   */
  @Event({ bubbles: true }) tabChanged: EventEmitter<FluidTabChange>;

  /**
   * Event emitted when a Tab is clicked that changes the selected Tab.
   */
  @Event({ bubbles: true }) tabHasChanged: EventEmitter<boolean>;

  /**
   * Event emitted when the Add Tab button is clicked
   */
  @Event({ bubbles: true }) addTabButtonClicked: EventEmitter<void>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback() {
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({
      language: () => {
        Utils.debug('[TABS] Language changed');
      },
    });
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action loadTabs - gather the fluid-tab elements as tab list
   * @action initializeSlotObserver - watch for changes in slotted Tab children
   * @action shouldShowTabNavigation - determine if left/right nav arrows shown
   */
  componentWillLoad(): void {
    this.loadTabs();
    this.initializeSlotObserver();

    readTask(() => {
      Utils.nextTick().then(() => {
        this.shouldShowTabNavigation();
      });
    });
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action initializeScrollListener - add event listener for horizontal scroll events
   * @action: initializeResizeObserver - begins monitoring Tabs for changes in size
   */
  componentDidLoad(): void {
    this.initializeScrollListener();
    this.initializeResizeObserver();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action: disconnectTabs - remove ResizeObserver / resize listener
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this.disconnectTabs();
    this._i18nStore?.deregisterElement(this._translateId);

    this._slotObserver?.disconnect();
    this._slotObserver = undefined;
  }

  // ================================================================ //
  // -- Event Listeners

  /**
   * When left/right arrow keys pressed, change :focus to next/previous
   * Tab button sibling. When tab key pressed, set :focus to currently
   * selected Tab button.
   */
  @Listen('keydown')
  onKeyDown(e: KeyboardEvent) {
    if (this.ignoreKeypress(e) || !this.tabCanChange) return;

    if (EventUtils.isKey(e, ARROW_KEYS)) {
      e.preventDefault();
      let targetBtn = this._activeBtn;

      do {
        // Find next/previous sibling that is NOT disabled
        targetBtn = EventUtils.isKey(e, [ARROW_UP, ARROW_LEFT])
          ? (targetBtn.previousElementSibling as HTMLElement)
          : (targetBtn.nextElementSibling as HTMLElement);
      } while (!!targetBtn && targetBtn.hasAttribute('disabled'));

      if (!!targetBtn && targetBtn !== this._activeBtn) {
        this._activeBtn = targetBtn as HTMLElement;
        this._activeBtn.focus();
      }
    } else if (EventUtils.isKey(e, TAB)) {
      // Reset "active" button to currently selected button
      this._activeBtn = this._selectedBtn;
    }
  }

  /**
   * When Tab button receives :focus, set _activeBtn reference to currently
   * selected Tab button so that left/right arrow keys can be used to navigate.
   */
  @Listen('focus')
  onFocus() {
    this._activeBtn = this._selectedBtn;
  }

  @Listen('disableChanged')
  onDisableChangedHandler(event: CustomEvent<boolean>) {
    this.setDisabled(event.target['id'], event.detail);
  }

  @Listen('titleChanged')
  onTitleChangedHandler() {
    this.loadTabs();
  }

  @Listen('hiddenChanged')
  onHiddenChangedHandler() {
    this.loadTabs();
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Will reload the tabs
   */
  @Method()
  async refreshTabs() {
    timer(500).subscribe(() => {
      this.loadTabs();
    });
  }

  /**
   * Will activate a specific tab by passing the tab id
   * @param tabId
   */
  @Method()
  async setActiveTab(tabId: string) {
    if (!this.tabs.every((t) => !!t.id)) {
      Utils.consoleWarn(this.host, NO_TAB_IDS);
      return;
    }

    const tab = this.tabs.find((t: HTMLFluidTabElement) => t.id === tabId);

    if (tab['hidden']) {
      Utils.consoleWarn(this.host, ACTIVE_HIDDEN_TAB(tabId));
      return;
    }

    const tabIdx: number = this.tabs.findIndex((t) => t.id === tabId);
    if (tabIdx >= 0) {
      this.setSelectedTab(this.tabs[tabIdx] as HTMLFluidTabElement);
    } else {
      Utils.consoleWarn(this.host, NO_TAB_WITH_ID(tabId));
    }
  }

  /**
   * Will activate the next Tab
   */
  @Method()
  async nextTab() {
    const activeId = this.tabs.findIndex((t: HTMLFluidTabElement) => t.active);
    this.setSelectedTab(this.tabs[activeId + 1] as HTMLFluidTabElement);
  }

  /**
   * Will activate the previous Tab
   */
  @Method()
  async previousTab() {
    const activeId = this.tabs.findIndex((t: HTMLFluidTabElement) => t.active);
    this.setSelectedTab(this.tabs[activeId - 1] as HTMLFluidTabElement);
  }

  /**
   * Will disable a specific tab by passing the tab id and the status true or false
   * @param tabId
   * @param disabled
   */
  @Method()
  async setDisabled(tabId: string, disabled: boolean) {
    this.tabs = [
      ...this.tabs.map((tab) => {
        if (tab.id === tabId) {
          tab.setAttribute('disabled', disabled.toString());
        }
        return tab;
      }),
    ];
  }

  /**
   * Will hide a specific Tab by passing the `tab id` and and the value true or false for hide or show.
   * @param tabId @type {string}
   * @param hidden @type {boolean}
   * @returns
   */
  @Method()
  async setTabVisibility(tabId: string, hidden: boolean) {
    const tabActive = this.tabs.find((t: HTMLFluidTabElement) => t.active);
    if (tabActive.id === tabId && hidden) {
      Utils.consoleWarn(this.host, HIDDEN_ACTIVE_TAB(tabId));
      return;
    }

    this.tabs = this.tabs.map((tab) => {
      if (tab.id === tabId) {
        tab['hidden'] = hidden;
      }
      return tab;
    });
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Sets up MutationObserver to watch for changes in slotted Tab
   * children. When change is detected, reloads references to children
   * & checks if "tab navigation" arrows should be shown.
   */
  initializeSlotObserver(): void {
    this._slotObserver = Utils.watchChildContent(
      this.host,
      () => {
        this.loadTabs();

        readTask(() => {
          Utils.nextTick().then(() => {
            this.shouldShowTabNavigation();
          });
        });
      },
      {
        childList: true,
        subtree: true,
      }
    );
  }

  /**
   * If ResizeObserver is supported, stops observing host & removes
   * instance. If ResizeObserver is NOT supported, removes "resize"
   * event listener from window.
   */
  disconnectTabs(): void {
    if (this._resizeObserverSupported) {
      this._resizeObserver?.disconnect();
    } else {
      window.removeEventListener('resize', this._resizeObserver);
    }
  }

  /**
   * Determines if keyboard event should be ignored based on event target.
   * Returns true (i.e. the event SHOULD be ignored) if the "keydown" event
   * occurred on anything in the "Tab body" (i.e. anything OTHER than the
   * "Tab buttons").
   */
  ignoreKeypress(e: KeyboardEvent): boolean {
    if (Utils.browserIsMsie()) {
      return !(e.target as HTMLElement).classList.contains('fluid-tab-btn');
    } else {
      return e.target !== this.host;
    }
  }

  loadTabs() {
    this.tabs = Array.from(this.host.children).filter(
      (t) => t.localName === 'fluid-tab'
    ) as HTMLFluidTabElement[];
    if (this.tabs.length === 0) {
      this.loadAsNodeList();
    }
    this.getActiveDefault();
  }

  /**
   * If we don't get fluid-tabs from DOM children, try with querySelectorAll and convert to elements.
   * If THAT doesn't work, throw an error, there are no tabs.
   */
  loadAsNodeList() {
    const tabElements = [];
    this.host
      .querySelectorAll('fluid-tab')
      ?.forEach((el) => tabElements.push(el));
    this.tabs = tabElements;
    if (this.tabs.length === 0) {
      throw new Error(
        '[fluid-tabs] You must supply at least one fluid-tab element!'
      );
    }
  }

  initializeScrollListener(): void {
    this._tabsList.addEventListener(
      'scroll',
      Utils.debounce(() => this.checkScrollPosition(), 100)
    );
  }

  /**
   * When component is resized, check for Tabs overflow & determine if nav
   * arrows should be shown.
   */
  initializeResizeObserver(): void {
    if (!this._resizeObserverSupported) {
      this._resizeObserver = Utils.throttle(() => {
        this.shouldShowTabNavigation();
      }, 300);
      window.addEventListener('resize', this._resizeObserver);
    } else {
      this._resizeObserver = new ResizeObserver(
        Utils.debounce(() => this.shouldShowTabNavigation())
      );
      this._resizeObserver.observe(this.host);
    }
  }

  checkScrollPosition(): void {
    this.scrollPosition = Math.ceil(this._tabsList.scrollLeft);
  }

  async shouldShowTabNavigation() {
    // Wait until next animation frame to give Tab Buttons time to render
    await Utils.nextTick();

    if (this.verticalLayout || this.disableNavigationArrows) {
      this.showArrows = false;
    } else if (this._tabsList && this._tabsListContainer) {
      const tabsContainerWidth = this._tabsListContainer.offsetWidth;
      const tabsWidth = this._tabsList.scrollWidth;
      // Subtract 1 from max position to ensure scrolling always hits the "end" of container
      const maxScrollPosition = tabsWidth - tabsContainerWidth - 1;

      this.showArrows = tabsWidth > tabsContainerWidth;
      this.maxScrollPosition = this.showArrows ? maxScrollPosition : 0;
      this.checkScrollPosition();
    }
  }

  getActiveDefault() {
    const active = this.tabs.find((tab: HTMLFluidTabElement) => tab.active);
    this.setSelectedTab((active || this.tabs[0]) as HTMLFluidTabElement);
  }

  selectTab(tab: HTMLFluidTabElement) {
    const previous: HTMLFluidTabElement = this.tabs.find(
      (t: HTMLFluidTabElement) => t.active
    ) as HTMLFluidTabElement;

    if (previous === tab) return; // skip when is the same tab
    this.tabChanged.emit({ previous, current: tab });

    if (this.debounceChange) {
      this.debounceChange.subscribe((canChange: boolean) => {
        if (canChange) {
          this.setSelectedTab(tab);
        }
      });
    } else if (this.tabCanChange) {
      this.setSelectedTab(tab);
    }
  }

  setSelectedTab(tab: HTMLFluidTabElement) {
    this.tabs = this.tabs.map((tab: HTMLFluidTabElement) => {
      tab.active = false;
      return tab;
    });
    tab.active = true;
    this.selectedTab = tab;
    this.tabHasChanged.emit(true);
  }

  scrollLeft() {
    this._tabsList.scrollLeft -= this._defaultScrollLeft;
  }

  scrollRight() {
    this._tabsList.scrollLeft += this._defaultScrollLeft;
  }

  /**
   * Sets references to currently selected/active (:focus) Tab button.
   */
  setSelectedBtn(tab: HTMLElement, btn: HTMLElement): void {
    if (tab !== this.selectedTab) return;
    if (btn !== this._selectedBtn) {
      this._activeBtn = btn;
    }
    this._selectedBtn = btn;
  }

  addTabDisabled() {
    if (Utils.isNullOrUndefined(this.maximumNumberOfTabs)) {
      return false;
    }
    return (
      this.maximumNumberOfTabs >= 0 &&
      this.tabs.length >= this.maximumNumberOfTabs
    );
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    if (this.tabs) {
      return (
        <div
          class={{
            [`fluid-tabs ${this.theme}`]: true,
            vertical: this.verticalLayout,
            horizontal: !this.verticalLayout,
          }}>
          <div
            id='tabs-container'
            class={{
              'tabs-container': true,
              horizontal: !this.verticalLayout,
              vertical: this.verticalLayout,
            }}>
            <div
              ref={(el: HTMLElement) => (this._tabsListContainer = el)}
              class={{
                'tabs-list-container': true,
                'vertical-layout': this.verticalLayout,
                'has-add-tab-btn': !!this.addTabBtnConfig,
              }}>
              <TabButtonsContainer
                setTabListRef={(el) => (this._tabsList = el)}
                setSelectedBtn={(tab, btn) => this.setSelectedBtn(tab, btn)}
                verticalLayout={this.verticalLayout}
                showArrows={this.showArrows}
                tabs={this.tabs}
                selectTab={(t) => this.selectTab(t)}
                selectedTab={this.selectedTab}
                tabCanChange={this.tabCanChange}
                theme={this.theme}
                i18nStore={this._i18nStore}
                fluidTranslate={this.fluidTranslate}
              />
              <fluid-tab-navigation
                scrollPosition={this.scrollPosition}
                maxScrollPosition={this.maxScrollPosition}
                showArrows={this.showArrows}
                onLeftScroll={() => this.scrollLeft()}
                onRightScroll={() => this.scrollRight()}
                theme={this.theme}
                overrideGlobalTheme={true}
              />
            </div>
            <AddTabButton
              config={this.addTabBtnConfig}
              verticalLayout={this.verticalLayout}
              disabled={this.addTabDisabled()}
              theme={this.theme}
              addTabButtonClicked={this.addTabButtonClicked}
            />
          </div>
          <div
            class={{
              'tabs-content': true,
              horizontal: !this.verticalLayout,
              vertical: this.verticalLayout,
            }}>
            <slot />
          </div>
        </div>
      );
    }
    return 'Loading Tabs...';
  }
}
